'use client';

import React from 'react';
import CobwebSvg from './CobwebSvg';

interface VictoryScreenProps {
  onPlayAgain: () => void;
}

export default function VictoryScreen({ onPlayAgain }: VictoryScreenProps) {
  return (
    <div className="relative min-h-screen bg-horror flex flex-col items-center justify-center overflow-hidden px-4 py-8">
      {/* Scanlines */}
      <div className="scanlines absolute inset-0 z-10 pointer-events-none" aria-hidden="true" />

      {/* Vignette */}
      <div className="candle-vignette absolute inset-0 z-10 pointer-events-none" aria-hidden="true" />

      {/* Cobwebs */}
      <CobwebSvg className="cobweb-tl z-20" />
      <CobwebSvg className="cobweb-tr z-20" />

      {/* Fog */}
      <div className="fog-layer z-10" aria-hidden="true" />

      {/* Creepy red hand emerging from darkness — subtly visible on the left side */}
      <div
        className="absolute bottom-0 left-0 z-20 pointer-events-none"
        style={{
          width: 'min(320px, 55vw)',
          height: 'min(420px, 70vw)',
          opacity: 0.35,
          maskImage: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 40%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 40%, transparent 100%)',
          filter: 'brightness(0.4) saturate(1.8) hue-rotate(340deg) contrast(1.3)',
        }}
        aria-hidden="true"
      >
        <img
          src="/assets/images/image-1785524964334.png"
          alt=""
          className="w-full h-full object-cover object-top"
          style={{ mixBlendMode: 'screen' }}
        />
      </div>

      {/* Main content */}
      <div className="relative z-30 flex flex-col items-center text-center max-w-md mx-auto w-full">

        {/* Congratulations title */}
        <h1 className="font-horror text-4xl sm:text-5xl md:text-6xl victory-glow mb-2 tracking-widest" style={{ color: 'var(--gold)' }}>
          SVEIKINAME!
        </h1>

        {/* Subtitle — Oswald font like room names */}
        <p
          className="text-2xl sm:text-3xl text-primary flicker-text mb-2 tracking-wide font-bold"
          style={{ fontFamily: '"Oswald", sans-serif' }}
        >
          Tu išgyvenai!
        </p>

        <p className="text-muted-foreground text-base sm:text-lg mb-8 leading-relaxed">
          Praėjai visus{' '}
          <span className="text-primary font-semibold">10 kambarių</span>!
          Tu tikras siaubo meistras.
        </p>

        {/* Prize box — screenshot-worthy */}
        <div
          className="gold-border-glow w-full rounded-2xl p-6 sm:p-8 mb-8 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #1a1200 0%, #120a00 50%, #0f0800 100%)',
            border: '3px solid var(--gold)',
          }}
          role="region"
          aria-label="Prizo žinutė"
        >
          {/* Inner glow */}
          <div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(255,215,0,0.05) 0%, transparent 70%)',
            }}
            aria-hidden="true"
          />

          {/* Prize text — Oswald font like room names, uniform size */}
          <p
            className="text-xl sm:text-2xl font-bold leading-relaxed relative z-10"
            style={{ color: 'var(--gold)', fontFamily: '"Oswald", sans-serif' }}
          >
            Šį sveikinimą parodykite kasoje ir gaukite nemokamų prizų!
          </p>

          {/* Gift and Camera icons — small, side by side at bottom */}
          <div className="flex items-center justify-center gap-3 mt-4 relative z-10" aria-hidden="true">
            <span className="text-2xl">🎁</span>
            <span className="text-2xl">📸</span>
          </div>

          {/* Decorative gold lines */}
          <div
            className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
            style={{ background: 'linear-gradient(90deg, transparent, var(--gold), transparent)' }}
            aria-hidden="true"
          />
          <div
            className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl"
            style={{ background: 'linear-gradient(90deg, transparent, var(--gold), transparent)' }}
            aria-hidden="true"
          />
        </div>

        {/* Play again button */}
        <button
          onClick={onPlayAgain}
          className="restart-btn text-lg sm:text-xl px-8 py-4 rounded-lg tracking-widest uppercase w-full max-w-xs cursor-pointer mb-4"
          aria-label="Žaisti dar kartą"
          style={{ touchAction: 'manipulation', minHeight: '56px', fontFamily: '"Oswald", sans-serif', fontWeight: 700 }}
        >
          Žaisti dar kartą
        </button>

        {/* Footer */}
        <p className="text-muted-foreground text-xs opacity-40 flicker-slow mt-2">
          2026 „Siaubo fabrikas". Visos teisės saugomos.
        </p>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black to-transparent pointer-events-none z-10" aria-hidden="true" />
    </div>
  );
}